import { Page } from "../enums/Page";
import { css, html } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { WordleService } from "../services/WordleService";

const styles: string = css`
    :host {
        position: absolute;
        top: 0;
        font-family: "Onesize";
    }
    
    #game-board {
        display: flex;
        align-items: center;
        flex-direction: column;
        margin-top: 20%;
    }

    .letter-box {
        border: 1px solid grey;
        border-radius: 3px;
        margin: 2px;
        font-size: 3rem;
        font-weight: 700;
        height: 3.5rem;
        width: 3.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        text-transform: uppercase;
    }

    .filled-box {
        border: 2px solid black;
    }

    .letter-row {
        display: flex;
    }

    .wrong-letter {
        background-color: grey;
    }

    .right-letter {
        background-color: green;
    }

    .almost-letter {
        background-color: #ffcc00;
    }
    
    #keyboard-cont {
        margin: 1rem 0;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    #keyboard-cont div {
        display: flex;
    }

    .second-row {
        margin: 0.5rem 0;
    }

    .keyboard-button {
        font-family: "Onesize";
        font-size: 1.5rem;
        font-weight: 700;
        padding: 0.5rem;
        margin: 0 2px;
        cursor: pointer;
        text-transform: uppercase;
    }

    .alert-container {
        position: absolute;
        top: 7%;
        left: 50%;
        width: 350px;
        transform: translate(-50%, -50%);
        border-radius: 10px;
        padding: 1px;
        visibility: hidden;
        text-align: center;
    }

    .alert-container.congrats {
        visibility: visible;
    }

    .alert-container.wrong {
        visibility: visible;
    }

    .alert-container.over {
        visibility: visible;
    }
    
    #alert-text {
        font-size: 1.5rem;
        font-weight: 700;
    }

    .alert-button {
        visibility: hidden;
        font-family: "Onesize";
        color: black;
        background-color: grey;
        transform: translate(-50%, -50%);
        position: absolute;
        top: 110%;
        left: 50%;
        height: 60px;
        width: 100px;
        border-radius: 10px;
        font-size: 1.5rem;
        font-weight: 700;
        cursor: pointer;
    }

    .alert-button.show {
        visibility: visible;
    }

    #game-instructions {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 450px;
        height: 450px;
        text-align: center;
        font-size: 1.5rem;
        background-color: green;
        opacity: 0.95;
        border-radius: 10px;
    }

    #close-button {
        font-family: "Onesize"; 
        font-size: 1.2rem;
        font-weight: 700;
        background-color: grey;
        border-radius: 5px;
        cursor: pointer;
        color: black;
        padding: 0.5rem;
    }
`;

/**
 * Web Component representing the Wordle game used in the Jukebox Room to guess the song title "Toxic".
 * @extends HTMLElement
 * @author Eva Biegel
 */
export class WordleComponent extends HTMLElement {
    private _numberOfGuesses: number = 6; // Player gets to guess 6 times
    private _guessesRemaining: number = this._numberOfGuesses;
    private _currentGuess: string[] = [];
    private _nextLetterIndex: number = 0;
    private _wordleService: WordleService = new WordleService();
    private _gameEventService: GameEventService = new GameEventService();

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
        this.handleUserInput();
        this.handleTryAgain();
        this.handleKeyboardInput();
        this.handleCloseInstructions();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        const element: HTMLElement = this.renderWordleComponent();

        this.shadowRoot.firstChild?.remove();
        this.shadowRoot.appendChild(element); // Append the wordle component to the shadow root

        // Add the game board to the shadow root
        const board: HTMLElement = this.renderBoard();
        this.shadowRoot.appendChild(board);

        // Add the keyboard to the shadow root
        const keyboard: HTMLElement = this.renderKeyboard();
        this.shadowRoot.appendChild(keyboard);

        // Add the alert to the shadow root
        const alert: HTMLElement = this.renderAlert();
        this.shadowRoot.appendChild(alert);

        // Add the try again button to the shadow root
        const button: HTMLElement = this.renderTryAgainButton();
        this.shadowRoot.appendChild(button);

        // Add the game instructions to the shadow root
        const instructions: HTMLElement = this.renderGameInstructions();
        this.shadowRoot.appendChild(instructions);
    }

    private renderWordleComponent(): HTMLElement {
        const element: HTMLElement = html`
            <style>
                ${styles}
            </style>
        `;

        return element;
    }

    /**
     * Renders the game board with 6 rows of 5 boxes each.
     * @returns HTMLElement representing the game board
     */
    private renderBoard(): HTMLElement {
        const board: HTMLElement = document.createElement("div");
        board.id = "game-board";

        for (let i: number = 0; i < this._numberOfGuesses; i++) {
            const row: HTMLDivElement = document.createElement("div");
            row.className = "letter-row";

            for (let j: number = 0; j < 5; j++) {
                const box: HTMLDivElement = document.createElement("div");
                box.className = "letter-box";
                row.appendChild(box);
            }

            board.appendChild(row);
        }

        return board;
    }

    /**
     * Renders the keyboard below the game board.
     * @returns HTMLElement representing the keyboard
     */
    private renderKeyboard(): HTMLElement {
        const keyboard: HTMLElement = html`
            <div id="keyboard-cont">
                <div class="first-row">
                    <button class="keyboard-button" id="key-q">q</button>
                    <button class="keyboard-button" id="key-w">w</button>
                    <button class="keyboard-button" id="key-e">e</button>
                    <button class="keyboard-button" id="key-r">r</button>
                    <button class="keyboard-button" id="key-t">t</button>
                    <button class="keyboard-button" id="key-y">y</button>
                    <button class="keyboard-button" id="key-u">u</button>
                    <button class="keyboard-button" id="key-i">i</button>
                    <button class="keyboard-button" id="key-o">o</button>
                    <button class="keyboard-button" id="key-p">p</button>
                </div>
                <div class="second-row">
                    <button class="keyboard-button" id="key-a">a</button>
                    <button class="keyboard-button" id="key-s">s</button>
                    <button class="keyboard-button" id="key-d">d</button>
                    <button class="keyboard-button" id="key-f">f</button>
                    <button class="keyboard-button" id="key-g">g</button>
                    <button class="keyboard-button" id="key-h">h</button>
                    <button class="keyboard-button" id="key-j">j</button>
                    <button class="keyboard-button" id="key-k">k</button>
                    <button class="keyboard-button" id="key-l">l</button>
                </div>
                <div class="third-row">
                    <button class="keyboard-button" id="key-del">Del</button>
                    <button class="keyboard-button" id="key-z">z</button>
                    <button class="keyboard-button" id="key-x">x</button>
                    <button class="keyboard-button" id="key-c">c</button>
                    <button class="keyboard-button" id="key-v">v</button>
                    <button class="keyboard-button" id="key-b">b</button>
                    <button class="keyboard-button" id="key-n">n</button>
                    <button class="keyboard-button" id="key-m">m</button>
                    <button class="keyboard-button" id="key-enter">Enter</button>
                </div>
            </div>
        `;
        return keyboard;
    }

    private renderAlert(): HTMLElement {
        const alert: HTMLElement = html`
            <div class="alert-container">
                <p id="alert-text"></p>
            </div>
        `;

        return alert;
    }

    private renderTryAgainButton(): HTMLElement {
        const button: HTMLElement = html`
            <button class="alert-button">Reset</button>
        `;

        return button;
    }

    private renderGameInstructions(): HTMLElement {
        const guide: HTMLElement = html`
            <div id="game-instructions">
                <p>Guess the 5-letter word to unlock the song!</p>
                <p>Press "Enter" to check your guess. Press "Del" to delete a letter.</p>
                <p>Green: right letter, right position</p>
                <p>Yellow: right letter, wrong position</p>
                <p>Grey: wrong letter</p>
                <button id="close-button">Let's play!</button>
            </div>
        `;

        return guide;
    }

    private handleCloseInstructions(): void {
        const instructions: Element | null = this.shadowRoot!.querySelector("#game-instructions");
        const closeButton: Element | null = this.shadowRoot!.querySelector("#close-button");

        if (instructions && closeButton) {
            closeButton.addEventListener("click", () => {
                instructions.remove();
            });
        }
    }

    /**
     * Handles user input by listening for key presses.
     */
    private handleUserInput(): void {
        document.addEventListener("keyup", e => {
            e.preventDefault();

            if (this._guessesRemaining === 0) return;

            const pressedKey: string = (e.key);

            if (pressedKey === "Backspace" && this._nextLetterIndex !== 0) {
                this.deleteLetter();
                return;
            }

            if (pressedKey === "Enter") {
                void this.checkGuess();
                return;
            }

            const found: boolean = this.isLetter(pressedKey);
            if (!found) return;
            else {
                this.insertLetter(pressedKey);
            }
        });
    }

    /**
     * Handles the insertion of a letter into the game board.
     * @param pressedKey string representing the key pressed by the user
     */
    private insertLetter(pressedKey: string): void {
        if (this._nextLetterIndex === 5) return; // Quit if the row is full

        pressedKey = pressedKey.toLowerCase();

        const row: Element = this.shadowRoot!.querySelectorAll(".letter-row")[6 - this._guessesRemaining];
        const box: Element = row.children[this._nextLetterIndex];
        box.textContent = pressedKey;
        box.classList.add("filled-box");
        this._currentGuess.push(pressedKey);
        this._nextLetterIndex += 1;
    }

    /**
     * Handles the deletion of a letter from the game board.
     */
    private deleteLetter(): void {
        const row: Element = this.shadowRoot!.querySelectorAll(".letter-row")[6 - this._guessesRemaining];
        const box: Element = row.children[this._nextLetterIndex - 1];
        box.textContent = "";
        box.classList.remove("filled-box");
        this._currentGuess.pop();
        this._nextLetterIndex -= 1;
    }

    /**
     * Compares the current guess with the right guess and updates the game board accordingly.
     */
    private async checkGuess(): Promise<void> {
        const row: Element = this.shadowRoot!.querySelectorAll(".letter-row")[6 - this._guessesRemaining];
        const guessString: string = this._currentGuess.join("");
        const rightGuess: string[] = Array.from("toxic"); // Convert the right guess to an array and store it in a variable
        const alertContainer: Element = this.shadowRoot!.querySelector(".alert-container")!;
        const alertText: Element = this.shadowRoot!.querySelector("#alert-text")!;
        const isGuessCorrect: boolean = await this._wordleService.checkIfWordIsCorrect(guessString);

        if (guessString.length < 5) {
            return;
        }

        // Check if the word exists in the words.ts file
        const isWordValid: boolean = await this._wordleService.checkIfWordIsValid(guessString);

        // Alert the user if the word is not in the list
        if (!isWordValid) {
            alertText.textContent = "That's not a valid word! Delete and try again.";
            alertContainer.classList.add("wrong");
            setTimeout(() => {
                alertContainer.classList.remove("wrong");
            }, 2500);
            return;
        }

        // Update the game board with colors to indicate right, wrong, or almost right letters
        const rowLength: number = 5;
        for (let i: number = 0; i < rowLength; i++) {
            const box: Element = row.children[i];
            let letterClass: string = "";
            const letterPosition: number = rightGuess.indexOf(this._currentGuess[i]);
            const guessedLetter: string = this._currentGuess[i];

            if (letterPosition === -1) {
                letterClass = "wrong-letter";
            }
            else {
                if (guessedLetter === rightGuess[i]) {
                    letterClass = "right-letter";
                }
                else {
                    letterClass = "almost-letter";
                }
                rightGuess[letterPosition] = "#";
            }

            // Add a delay to the coloring of the boxes so they don't all change at once
            setTimeout(() => {
                box.classList.add(letterClass);
                this.shadeKeyboard(guessedLetter, letterClass);
            }, 250 * i);
        }

        // Alert the user if they guessed the right word
        if (isGuessCorrect) {
            setTimeout(() => {
                alertText.textContent = "Congratulations! You've guessed the right word!";
                alertContainer.classList.add("congrats");
            }, 250 * rowLength);
            // Give the user some time to see the alert before switching back to the jukebox room
            setTimeout(() => {
                this._gameEventService.switchPage(Page.Canvas);
            }, 4000);
            this._guessesRemaining = 0;
            return;
        }
        // If the user has not guessed the right word, update the guesses remaining and reset the current guess
        else {
            this._guessesRemaining -= 1;
            this._currentGuess = [];
            this._nextLetterIndex = 0;
        }

        // Alert the user if they have run out of guesses
        if (this._guessesRemaining === 0) {
            const alertButton: Element = this.shadowRoot!.querySelector(".alert-button")!;
            alertButton.classList.add("show");
            return;
        }
    }

    /**
     * Adds an event listener to the try again button to allow the user to play again.
     */
    private handleTryAgain(): void {
        const alertButton: Element = this.shadowRoot!.querySelector(".alert-button")!;

        alertButton.addEventListener("click", () => {
            this._guessesRemaining = this._numberOfGuesses; // Reset the number of guesses the user has left to 6
            this._currentGuess = []; // Reset the current guess
            this._nextLetterIndex = 0; // Reset to the first box
            const keyboard: NodeListOf<Element> = this.shadowRoot!.querySelectorAll(".keyboard-button");
            // Reset the keyboard buttons to their original color
            keyboard.forEach(button => {
                button.classList.remove("right-letter", "wrong-letter", "almost-letter");
            });

            const boxes: NodeListOf<Element> = this.shadowRoot!.querySelectorAll(".filled-box");
            boxes.forEach(box => {
                box.textContent = "";
                box.classList.remove("filled-box", "wrong-letter", "right-letter", "almost-letter");
            });

            alertButton.classList.remove("show");
        });
    }

    /**
     * Changes the color of the keyboard buttons to indicate whether the letter is not in the word, is in the word but in the wrong position, or is in the word and in the right position.
     * @param letter string representing the letter pressed by the user
     * @param className string representing the class to add to the button
     */
    private shadeKeyboard(letter: string, className: string): void {
        const button: Element | null = this.shadowRoot!.querySelector(`#key-${letter}`);

        if (button) {
            if (button.classList.contains("right-letter")) return;
            if (button.classList.contains("almost-letter") && className === "wrong-letter") return;

            button.classList.remove("right-letter", "almost-letter", "wrong-letter");
            button.classList.add(className);
        }
    }

    /**
     * Handles the user input from the on-screen keyboard.
     */
    private handleKeyboardInput(): void {
        if (!this.shadowRoot) return;

        // Select the on-screen keyboard container
        const keyboard: Element = this.shadowRoot.querySelector("#keyboard-cont")!;

        keyboard.addEventListener("click", e => {
            const target: Element = e.target as Element; // Get the element that was clicked
            let key: string = target.textContent as string; // Get the text content of the clicked element

            // Convert "Del" to "Backspace" to match the keyup event
            if (key === "Del") {
                key = "Backspace";
            }
            // Simulate a keyup event with the key that was clicked
            document.dispatchEvent(new KeyboardEvent("keyup", { key }));
        });
    }

    /**
     * Handles the checking of whether the pressed key is a letter.
     * @param pressedKey string representing the key pressed by the user
     * @returns boolean
     */
    private isLetter(pressedKey: string): boolean {
        return /^[a-zA-Z]$/.test(pressedKey);
    }
}
